import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  getDocs,
  getDoc,
} from 'firebase/firestore'
import React, {useEffect, useState, useCallback} from 'react'
import Drawer from 'react-modern-drawer'
import db from '../../../../services/firebase'
import {Link, useNavigate} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../../../modules/auth'
import {ToastContainer} from 'react-toastify'
import OffboardingChecklist from './subcomponents/OffboardingChecklist'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import OwnerDisplay from 'app/components/OwnerDisplay'
import OffboardModal from './OffboardModal'
import useAuditContext from 'app/Hooks/useAuditContext'
import {createIntegrationNotifications} from '../services/IntegrationNotifications'

interface ViewPersonnelProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

interface UserData {
  uid: string
  id: string
  full_name: string
  role: string
  avatarUrl: string
  tenantId: string
  policiesAccepted?: boolean
  start_date: string
  group: string
  end_date: string
  email: string
  acceptedPolicies: string
  securityTraining: string
  image_url: string
}

interface FormErrors {
  end_date?: string
}

const ViewPersonnel: React.FC<ViewPersonnelProps> = ({isOpen, onClose, id}) => {
  const [users, setUsers] = useState<UserData[]>([])
  const [showModal, setShowModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<UserData | null>(null)
  const [groups, setGroups] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState('onboarding')
  const [assets, setAssets] = useState<any[]>([])
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const handleClose = () => setShowModal(false)
  const [end_date, setEndDate] = useState('')
  const [tasks, setTasks] = useState<any[]>([])
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [isTaskDeleteModalOpen, setIsTaskDeleteModalOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [isTaskDeleting, setIsTaskDeleting] = useState(false)
  const [isRoleChangeModalOpen, setIsRoleChangeModalOpen] = useState(false)
  const [roleChangeData, setRoleChangeData] = useState<{
    userId: string
    newRole: string
    originalRole: string
  } | null>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const {currentAuditContext} = useAuditContext()

  const getPersonnel = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'employees')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...(doc.data() as UserData),
            id: doc.id,
            end_date: doc.data().end_date,
            uid: doc.data().uid,
            full_name: doc.data().full_name,
          }))
          .filter((data) => data.id === id)
        setUsers(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting personnels:', error)
    }
  }, [currentUser?.tenantId, id])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'personnel_group')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      setGroups(risksData)
    })

    return unsubscribe
  }, [currentUser?.tenantId])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)
        const PolicyRef = collection(tenantRef, 'assets')

        const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
          const assetData = snapshot.docs
            .map((item) => ({
              ...item.data(),
              id: item.id,
              is_repo: item.data().is_repo || false,
              uid: item.data().uid || '',
              owner: item.data().owner || '',
            }))
            .filter((data) => data.is_repo !== true && data.owner === users[0]?.full_name)
          setAssets(assetData)
        })

        return unsubscribe
      } catch (error) {
        console.error('Error fetching assets:', error)
      }
    }

    fetchData()
  }, [users, currentUser?.tenantId])

  const handleCloseDrawer = () => {
    onClose()
    setActiveTab('onboarding')
  }

  const handleMarkAsComplete = async (taskId) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
      await updateDoc(taskRef, {status: 'Completed'})
      getTaskLists()
    } catch (error) {
      console.error('Error updating task status:', error)
    }
  }
  const handleMarkAsIncomplete = async (taskId) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', taskId)
      await updateDoc(taskRef, {status: 'Incomplete'})
      getTaskLists()
    } catch (error) {
      console.error('Error updating task status:', error)
    }
  }

  const getTaskLists = useCallback(async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'tasklists')

      const unsubscribe = onSnapshot(controlsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          owner: doc.data().owner || '',
        }))

        if (users && users.length > 0 && users[0]?.full_name) {
          setTasks(controlsData.filter((data) => data.owner === users[0].full_name))
        } else {
          console.error('User data is missing or incomplete')
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }, [currentUser?.tenantId, users])

  const validateForm = () => {
    const errors: FormErrors = {}
    if (!end_date.trim()) {
      errors.end_date = 'End Date is required'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleRemoveTask = (taskId: string) => {
    setSelectedTaskId(taskId)
    setIsTaskDeleteModalOpen(true)
  }

  const handleConfirmTaskDelete = async () => {
    if (!selectedTaskId) return

    try {
      setIsTaskDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const taskRef = doc(db, 'tenants', tenantId, 'tasklists', selectedTaskId)
      await deleteDoc(taskRef)
      getTaskLists() // Refresh the tasks list
      showSuccessToast({message: 'Selected items have been removed successfully.'})
    } catch (error) {
      showErrorToast({message: 'An error occurred while removing the selected items.'})
    } finally {
      setIsTaskDeleting(false)
      setIsTaskDeleteModalOpen(false)
      setSelectedTaskId(null)
    }
  }

  const handleOffboard = (item) => (event) => {
    setShowModal(true)
    setSelectedItem(item)
  }

  const handleConfirmOffboard = async () => {
    if (!validateForm()) {
      return
    }

    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      setIsLoading(true)
      const assetRef = doc(tenantRef, 'employees', id)
      await updateDoc(assetRef, {end_date: end_date})
      setIsLoading(false)
      setShowModal(false)
      setEndDate('')
      showSuccessToast({message: ` ${users[0].full_name} has been offboarded successfully.`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while updating the personnel.'})
      setIsLoading(false)
    }
  }

  const handleChangeEndDate = (e) => {
    setEndDate(e.target.value)

    setFormErrors((prev) => ({...prev, endDate: ''}))

    if (!e.target.value.trim()) {
      setFormErrors((prev) => ({...prev, endDate: 'End Date is required'}))
    }
  }

  const handleChange = async (e, id) => {
    const newStartDate = e.target.value
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)

    const assetRef = doc(tenantRef, 'employees', id)
    await updateDoc(assetRef, {start_date: e.target.value})
    if (selectedItem) {
      setSelectedItem({
        ...selectedItem,
        start_date: newStartDate,
      })
    }
    showSuccessToast({message: `The start date has been successfully changed.`})
  }

  const handleGroupChange = async (e, id) => {
    const newGroup = e.target.value
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)

    const assetRef = doc(tenantRef, 'employees', id)
    await updateDoc(assetRef, {group: e.target.value})
    if (selectedItem) {
      setSelectedItem({
        ...selectedItem,
        group: newGroup,
      })
    }
  }

  const handleTabClick = (tab) => {
    setActiveTab(tab)
  }

  const handleRoleChange = (e, userId) => {
    const newRole = e.target.value
    const originalRole = users.find((user) => user.id === userId)?.role || ''
    setRoleChangeData({userId, newRole, originalRole})
    setIsRoleChangeModalOpen(true)
  }

  const handleConfirmRoleChange = async () => {
    if (!roleChangeData) return

    const {userId, newRole} = roleChangeData
    setUpdating(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = doc(tenantRef, 'employees', userId)

      const userDoc = await getDoc(assetRef)
      if (userDoc.exists()) {
        const userData = userDoc.data()
        const currentRole = userData.role

        await updateDoc(assetRef, {role: newRole})

        if (currentRole === 'Admin' && newRole === 'Member') {
          const collectionsToUpdate = [
            'tests',
            'controls',
            'policies',
            'risks',
            'vendors',
            'assets',
            'tasklists',
          ]

          for (let collectionName of collectionsToUpdate) {
            const collectionRef = collection(db, 'tenants', tenantId, collectionName)
            let querySnapshot

            if (collectionName === 'controls') {
              querySnapshot = await getDocs(
                query(collectionRef, where('control_owner', '==', userData.full_name))
              )
            } else {
              querySnapshot = await getDocs(
                query(collectionRef, where('owner', '==', userData.full_name))
              )
            }

            querySnapshot.forEach(async (docSnapshot) => {
              const docRef = doc(collectionRef, docSnapshot.id)
              const docData = docSnapshot.data()
              if (
                docData.owner === userData.full_name ||
                docData.control_owner === userData.full_name
              ) {
                const updateField = collectionName === 'controls' ? 'control_owner' : 'owner'
                await updateDoc(docRef, {[updateField]: ''})
              }
            })
          }
        }
        showSuccessToast({message: `The role has been successfully changed to ${newRole}.`})
      } else {
        showErrorToast({message: 'User document not found in Firestore.'})
      }
    } catch (error) {
      showErrorToast({
        message:
          'An error occurred while updating the role or removing the user from other collections.',
      })
    } finally {
      setUpdating(false)
      setIsRoleChangeModalOpen(false)
      setRoleChangeData(null)
    }
  }

  const getIntegration = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'integrations')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const integrationData = snapshot.docs.map((item) => ({
          ...item.data(),
          id: item.id,
          name: item.data().name,
        }))
        setIntegration(integrationData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting integrations:', error)
    }
  }, [currentUser?.tenantId])

  useEffect(() => {
    getPersonnel()
    getIntegration()
  }, [getPersonnel, getIntegration])

  useEffect(() => {
    if (users.length > 0) {
      getTaskLists()
    }
  }, [users, getTaskLists])

  const sendReminder = async (task: any, employee: UserData) => {
    try {
      const notificationService = createIntegrationNotifications(integration, currentUser)

      const result = await notificationService.sendTaskReminder(task, employee)

      if (result.success) {
        showSuccessToast({
          message: `Task reminder sent successfully via ${result.successfulIntegrations.join(
            ', '
          )}`,
        })
      } else {
        showErrorToast({
          message: `Failed to send task reminder: ${result.errors.join(', ')}`,
        })
      }
    } catch (error: any) {
      console.error('Error sending task reminder:', error)
      showErrorToast({message: 'Error sending task reminder'})
    }
  }

  return (
    <>
      <Drawer open={isOpen} onClose={handleCloseDrawer} direction='right' className='' size='45%'>
        <div
          className=' modal-dialog-centered border-bottom border-gray-300'
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            padding: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingLeft: '15px',
            paddingRight: '12px',
          }}
        >
          <div className='fs-4 fw-bold mx-2' style={{margin: 0}}>
            Employee Details
          </div>

          <div style={{display: 'flex', gap: '16px'}}>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary '
              onClick={onClose}
              aria-label='Close'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#5A5A5A'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-x'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M18 6l-12 12' />
                <path d='M6 6l12 12' />
              </svg>
            </div>
          </div>
        </div>
        <div
          className='p-6 scroll-y overflow-auto '
          tabIndex={-1}
          style={{height: 'calc(100vh - 30px)'}}
        >
          {users.map((user) => (
            <>
              <div key={user.id}>
                <div className='modal-body scroll-y '>
                  <div>
                    <div className='d-flex justify-content-between mb-8'>
                      <div className='symbol symbol-40px me-5'>
                        {user.full_name ? (
                          <>
                            {user.image_url ? (
                              <img
                                src={user.image_url}
                                alt={user.full_name}
                                className='symbol-label fs-6 fw-bold me-2'
                              />
                            ) : (
                              <div className='symbol symbol-30px me-2'>
                                <span
                                  className='symbol-label fs-6 fw-bold'
                                  style={{backgroundColor: '#F5F5F5', border: '1px solid #E5E5E5'}}
                                >
                                  {user.full_name
                                    ? user.full_name.trim().charAt(0).toUpperCase()
                                    : ''}
                                </span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <span>--</span>
                          </>
                        )}
                      </div>
                      <div className='d-flex flex-column'>
                        <span className='fw-bold fs-6'>{user.full_name}</span>
                        <span className=' fw-semibold d-block fs-7' style={{color: '#5A5A5A'}}>
                          {user.email}
                        </span>
                      </div>

                      <div className='ms-auto'>
                        {(!currentAuditContext || currentUser?.role === 'Auditor') &&
                          // Owner can offboard themselves only
                          ((currentUser?.role === 'Owner' && user.id === currentUser.uid) ||
                            // Admin can offboard Members only
                            (currentUser?.role === 'Admin' && user.role === 'Member') ||
                            // Admin or Owner can offboard Members and Admins (but not Owners except themselves)
                            ((currentUser?.role === 'Admin' || currentUser?.role === 'Owner') &&
                              (user.role === 'Member' || user.role === 'Admin') &&
                              user.id !== currentUser.uid)) && (
                            <div style={{cursor: 'pointer'}}>
                              <button
                                className='btn btn-sm fs-6'
                                onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                                  (e.currentTarget.style.backgroundColor = '#148363')
                                }
                                onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                                  (e.currentTarget.style.backgroundColor = '#0E9F6E')
                                }
                                style={{
                                  backgroundColor: '#0E9F6E',
                                  color: '#fff',
                                  height: '40px',
                                  borderRadius: '4px',
                                  left: '615px',
                                  top: '20px',
                                  transition: 'background-color 0.3s',
                                }}
                                onClick={handleOffboard(user)}
                                disabled={!!user.end_date}
                              >
                                Offboard {user.full_name}
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <OffboardModal
                show={showModal}
                onClose={handleClose}
                onConfirm={handleConfirmOffboard}
                onChangeEndDate={handleChangeEndDate}
                user={user}
                end_date={end_date}
                formErrors={formErrors}
                isLoading={isLoading}
              />

              {currentUser?.role === 'Owner' && user.id !== currentUser.uid ? (
                <div className='row mb-3'>
                  <div className='col-3 d-flex align-items-center'>
                    <label className='text-gray-800 fw-semibold fs-6 me-4'>Role</label>
                  </div>
                  <div className='col-9'>
                    <div className='fw-normal d-flex align-items-center'>
                      <select
                        name='role'
                        className='form-select w-50 p-2'
                        data-control='select2'
                        data-placeholder='Select Role'
                        value={user.role}
                        onChange={(e) => handleRoleChange(e, user.id)}
                      >
                        <option value='Admin'>Admin</option>
                        <option value='Member'>Member</option>
                      </select>
                      {updating && <span className='spinner-border spinner-border-sm ms-2'></span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className='row mb-3'>
                  <div className='col-3'>
                    <label className='text-gray-800 fw-semibold me-2 fs-6'>Role</label>
                  </div>
                  <div className='col-9'>
                    <span className='fw-normal fw-bold'>{user.role}</span>
                  </div>
                </div>
              )}

              <div className='row mb-3'>
                <div className='col-3 d-flex align-items-center'>
                  <label className='text-gray-800 fw-semibold fs-6 '>Start Date</label>
                </div>
                <div className='col-9'>
                  <span className='fw-normal'>
                    {' '}
                    <input
                      placeholder='Start Date'
                      className='form-control w-50 p-2'
                      type='date'
                      name='startDate'
                      autoComplete='off'
                      onChange={(e) => handleChange(e, user.id)}
                      value={user.start_date}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                    />
                  </span>
                </div>
              </div>
              <div className='row mb-3'>
                <div className='col-3 d-flex align-items-center'>
                  <label className='text-gray-800 fw-semibold fs-6 me-4'>Group</label>
                </div>
                <div className='col-9'>
                  <span className='fw-normal'>
                    {' '}
                    <select
                      name='group'
                      className='form-select  w-50 p-2 '
                      data-control='select2'
                      data-placeholder='Select Group'
                      value={user.group || ''}
                      onChange={(e) => handleGroupChange(e, user.id)}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                    >
                      {groups.map((group) => (
                        <option key={group.id} value={group.groupName}>
                          {group.groupName}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
              </div>

              <div className='px-2 '>
                <div className='d-flex  h-55px border-bottom'>
                  <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-semibold flex-nowrap '>
                    <li className='nav-item'>
                      <Link
                        to={`/employees`}
                        className={`nav-link me-6 ${
                          activeTab === 'onboarding' ? 'text-active-primary active' : ''
                        }`}
                        onClick={() => handleTabClick('onboarding')}
                      >
                        Onboarding
                      </Link>
                    </li>
                    <li className='nav-item'>
                      <Link
                        to={``}
                        className={`nav-link me-6 ${
                          activeTab === 'tasks' ? 'text-active-primary active' : ''
                        }`}
                        onClick={() => handleTabClick('tasks')}
                      >
                        Tasks
                      </Link>
                    </li>
                    <li className='nav-item'>
                      <Link
                        to={'/employees'}
                        className={`nav-link me-6 ${
                          activeTab === 'assets' ? 'text-active-primary active' : ''
                        }`}
                        onClick={() => handleTabClick('assets')}
                      >
                        Assets
                      </Link>
                    </li>
                    {user.end_date && currentUser?.role === 'Owner' ? (
                      <li className='nav-item'>
                        <Link
                          to={'/employees'}
                          className={`nav-link me-6 ${
                            activeTab === 'offboarding' ? 'text-active-primary active' : ''
                          }`}
                          onClick={() => handleTabClick('offboarding')}
                        >
                          Offboarding
                        </Link>
                      </li>
                    ) : (
                      <></>
                    )}
                  </ul>
                </div>

                {activeTab === 'tasks' && (
                  <div className='mt-8 '>
                    <div
                      className='table-responsive'
                      style={{minHeight: '300px', maxHeight: '500px', overflowY: 'auto'}}
                    >
                      <table className='table table-row-dashed table-row-gray-300 align-middle '>
                        <thead>
                          <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                            <th className='w-25px'></th>
                            <th className='min-w-200px'>Task Name</th>
                            <th className='min-w-50px'>Priority</th>
                            <th className='min-w-100px'>Assigned To</th>
                            <th className='min-w-0px text-end'></th>
                          </tr>
                        </thead>
                        <tbody>
                          {tasks && tasks.length > 0 ? (
                            tasks.map((task) => (
                              <tr key={task.id}>
                                <td>
                                  <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                    <input
                                      className='form-check-input widget-9-check'
                                      type='checkbox'
                                      checked={task.status === 'Completed'}
                                      onChange={() => {
                                        if (task.status === 'Completed') {
                                          handleMarkAsIncomplete(task.id)
                                        } else {
                                          handleMarkAsComplete(task.id)
                                        }
                                      }}
                                      disabled={
                                        currentUser?.role === 'Auditor' || !!currentAuditContext
                                      }
                                    />
                                  </div>
                                </td>

                                <td>
                                  <div className='text-dark fw-semibold d-block fs-6'>
                                    {task.task_name}
                                  </div>
                                </td>
                                <td>
                                  <span className='badge border p-1.5 rounded-pill fs-7 fw-semibold'>
                                    <span
                                      className={`badge border rounded-circle mx-1  ${
                                        task.priority === 'High'
                                          ? 'bg-danger'
                                          : task.priority === 'Medium'
                                          ? 'bg-warning'
                                          : 'bg-primary'
                                      }`}
                                      style={{
                                        width: '12px',
                                        height: '12px',
                                        padding: '3px',
                                      }}
                                    >
                                      {' '}
                                    </span>

                                    {task.priority}
                                  </span>
                                </td>
                                <td>
                                  <div className='d-flex align-items-center'>
                                    {task.owner ? (
                                      <OwnerDisplay owner={task.owner} />
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </div>
                                </td>

                                <td className='text-end'>
                                  <div className='dropdown '>
                                    {currentUser?.role !== 'Auditor' && !currentAuditContext && (
                                      <button
                                        className='btn btn-secondary border-0 p-0 '
                                        type='button'
                                        id='dropdownMenu2'
                                        data-bs-toggle='dropdown'
                                        aria-haspopup='true'
                                        aria-expanded='false'
                                      >
                                        <img src='/media/buttons/Options.svg' alt='Options' />
                                      </button>
                                    )}
                                    <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                                      <li>
                                        {task.status === 'Completed' ? (
                                          <button
                                            className='dropdown-item text-hover-primary'
                                            style={{color: '#5A5A5A'}}
                                            type='button'
                                            onClick={() => handleMarkAsIncomplete(task.id)}
                                            disabled={currentUser?.role === 'Auditor'}
                                          >
                                            <svg
                                              xmlns='http://www.w3.org/2000/svg'
                                              width='18'
                                              height='18'
                                              viewBox='0 0 24 24'
                                              fill='none'
                                              stroke='currentColor'
                                              stroke-width='2'
                                              stroke-linecap='round'
                                              stroke-linejoin='round'
                                              className='icon icon-tabler icons-tabler-outline icon-tabler-x mx-1 mb-1'
                                            >
                                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                              <path d='M18 6l-12 12' />
                                              <path d='M6 6l12 12' />
                                            </svg>{' '}
                                            Mark as Incomplete
                                          </button>
                                        ) : (
                                          <button
                                            className='dropdown-item text-hover-primary'
                                            style={{color: '#5A5A5A'}}
                                            type='button'
                                            onClick={() => handleMarkAsComplete(task.id)}
                                            disabled={currentUser?.role === 'Auditor'}
                                          >
                                            <svg
                                              xmlns='http://www.w3.org/2000/svg'
                                              width='18'
                                              height='18'
                                              viewBox='0 0 24 24'
                                              fill='none'
                                              stroke='#5E6274'
                                              strokeWidth='2'
                                              strokeLinecap='round'
                                              strokeLinejoin='round'
                                              className='icon icon-tabler icons-tabler-outline icon-tabler-check mx-1 mb-1'
                                            >
                                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                              <path d='M5 12l5 5l10 -10' />
                                            </svg>{' '}
                                            Mark as complete
                                          </button>
                                        )}
                                      </li>
                                       {createIntegrationNotifications(
                                         integration,
                                         currentUser
                                       ).isReminderAvailable() &&
                                         !['Completed', 'completed', 'COMPLETED'].includes(task.status) && (
                                          <li>
                                            <button
                                              className='dropdown-item text-hover-primary'
                                              type='button'
                                              style={{color: '#262626'}}
                                              onClick={() => sendReminder(task, users[0])}
                                            >
                                              <svg
                                                xmlns='http://www.w3.org/2000/svg'
                                                width='18'
                                                height='18'
                                                viewBox='0 0 24 24'
                                                fill='none'
                                                stroke='currentColor'
                                                stroke-width='2'
                                                stroke-linecap='round'
                                                stroke-linejoin='round'
                                                className='icon icon-tabler icons-tabler-outline icon-tabler-brand-slack mx-1 mb-1'
                                              >
                                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                                <path d='M12 12v-6a2 2 0 0 1 4 0v6m0 -2a2 2 0 1 1 2 2h-6' />
                                                <path d='M12 12h6a2 2 0 0 1 0 4h-6m2 0a2 2 0 1 1 -2 2v-6' />
                                                <path d='M12 12v6a2 2 0 0 1 -4 0v-6m0 2a2 2 0 1 1 -2 -2h6' />
                                                <path d='M12 12h-6a2 2 0 0 1 0 -4h6m-2 0a2 2 0 1 1 2 -2v6' />
                                              </svg>{' '}
                                              Send Task Reminder
                                            </button>
                                          </li>
                                        )}
                                      <li>
                                        <button
                                          className='dropdown-item'
                                          style={{color: '#F15363'}}
                                          type='button'
                                          onClick={() => handleRemoveTask(task.id)}
                                          disabled={currentUser?.role === 'Auditor'}
                                        >
                                          <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            width='18'
                                            height='18'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            stroke='#F15363'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                                          >
                                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                            <path d='M4 7l16 0' />
                                            <path d='M10 11l0 6' />
                                            <path d='M14 11l0 6' />
                                            <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                            <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                          </svg>{' '}
                                          Remove task
                                        </button>
                                      </li>
                                      {/* <li>
                                        <button
                                          className='dropdown-item'
                                          type='button'
                                          style={{color: '#0E9F6E'}}
                                          onClick={() => setReminder(task.id, true)}
                                          disabled={currentUser?.role === 'Auditor'}
                                        >
                                          <svg
                                            width='24'
                                            height='25'
                                            viewBox='0 0 24 25'
                                            fill='none'
                                            xmlns='http://www.w3.org/2000/svg'
                                          >
                                            <path
                                              d='M12 10.3252V13.3252H14M7 4.3252L4.25 6.3252M17 4.3252L19.75 6.3252M5 13.3252C5 14.2444 5.18106 15.1547 5.53284 16.004C5.88463 16.8533 6.40024 17.6249 7.05025 18.2749C7.70026 18.925 8.47194 19.4406 9.32122 19.7924C10.1705 20.1441 11.0807 20.3252 12 20.3252C12.9193 20.3252 13.8295 20.1441 14.6788 19.7924C15.5281 19.4406 16.2997 18.925 16.9497 18.2749C17.5998 17.6249 18.1154 16.8533 18.4672 16.004C18.8189 15.1547 19 14.2444 19 13.3252C19 12.4059 18.8189 11.4957 18.4672 10.6464C18.1154 9.79713 17.5998 9.02546 16.9497 8.37545C16.2997 7.72544 15.5281 7.20982 14.6788 6.85804C13.8295 6.50626 12.9193 6.3252 12 6.3252C11.0807 6.3252 10.1705 6.50626 9.32122 6.85804C8.47194 7.20982 7.70026 7.72544 7.05025 8.37545C6.40024 9.02546 5.88463 9.79713 5.53284 10.6464C5.18106 11.4957 5 12.4059 5 13.3252Z'
                                              stroke='#0E9F6E'
                                              stroke-width='2'
                                              stroke-linecap='round'
                                              stroke-linejoin='round'
                                            />
                                          </svg>{' '}
                                          Set Reminder
                                        </button>
                                      </li> */}
                                    </ul>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <td colSpan={10}>
                              <div
                                className='d-flex justify-content-center align-items-center text-center'
                                style={{height: '100%'}}
                              >
                                <div className='m-8'>
                                  <img src='/media/buttons/emptystate.svg' alt='' />
                                  <div className='fs-5'>
                                    <p style={{fontWeight: 'bold'}}>No tasks added yet</p>
                                  </div>
                                  <p>Start by adding a new task to get things moving.</p>
                                  <a href='/dashboard'>
                                    {' '}
                                    <button
                                      type='button'
                                      className=' btn btn-sm btn-light me-4 fs-6'
                                      style={{
                                        height: '40px',
                                        background: '#fff',
                                        color: '#000',
                                        border: '1px solid #C5C5C5',
                                      }}
                                    >
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='20'
                                        height='20'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        stroke-width='2'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-plus me-2'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M12 5l0 14' />
                                        <path d='M5 12l14 0' />
                                      </svg>
                                      Add Task
                                    </button>
                                  </a>
                                </div>
                              </div>
                            </td>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {activeTab === 'onboarding' &&
                  users.map((user) => (
                    <>
                      <div className='mt-8 '>
                        <div className='card mb-5 mb-xl-8'>
                          <div className='card-header border-0 pt-5 mb-5'>
                            <div className='d-flex align-items-center'>
                              <div className='symbol symbol-35px me-5 '>
                                <img src={toAbsoluteUrl(`/media/logos/policies1.svg`)} alt='' />
                              </div>
                              <div className='d-flex justify-content-start flex-column'>
                                <span className='card-label fw-bold fs-6'>Accepted Policies</span>
                                <span
                                  className='fw-semibold fs-7 d-none d-md-block'
                                  style={{color: '#5A5A5A'}}
                                >
                                  Read & accepted all assigned policies
                                </span>
                              </div>
                            </div>

                            {user.acceptedPolicies === 'Complete' ? (
                              <div className='d-flex justify-content-end '>
                                <div className='pt-4 '>
                                  <span
                                    className='badge rounded-pill p-1'
                                    style={{border: '1px solid #C5C5C5'}}
                                  >
                                    <span className='mx-2'>
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='20'
                                        height='20'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='#0E9F6E'
                                        stroke-width='2'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-circle-check'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                                        <path d='M9 12l2 2l4 -4' />
                                      </svg>
                                    </span>
                                    <span className='fs-7 me-2' style={{color: '#5A5A5A'}}>
                                      Completed
                                    </span>
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className='d-flex justify-content-end '>
                                <div className='pt-4 '>
                                  <span
                                    className='badge rounded-pill p-1'
                                    style={{border: '1px solid #C5C5C5'}}
                                  >
                                    <span className='mx-2'>
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='18'
                                        height='18'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='#007bff'
                                        stroke-width='2'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-clock'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                                        <path d='M12 7v5l3 3' />
                                      </svg>
                                    </span>
                                    <span className='fs-7 me-2' style={{color: '#5A5A5A'}}>
                                      Pending
                                    </span>
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className='card mb-5 mb-xl-8'>
                          <div className='card-header border-0 pt-5 mb-5'>
                            <div className='d-flex align-items-center'>
                              <div className='symbol symbol-35px me-5 '>
                                <img src={toAbsoluteUrl(`/media/logos/training.svg`)} alt='' />
                              </div>
                              <div className='d-flex justify-content-start flex-column'>
                                <span className='card-label fw-bold fs-6'>Security Training</span>
                                <span
                                  className='fw-semibold fs-7 d-none d-md-block'
                                  style={{color: '#5A5A5A'}}
                                >
                                  Completed Security training
                                </span>
                              </div>
                            </div>

                            {user.securityTraining === 'Complete' ? (
                              <div className='d-flex justify-content-end '>
                                <div className='pt-4'>
                                  <span
                                    className='badge rounded-pill p-1'
                                    style={{border: '1px solid #C5C5C5'}}
                                  >
                                    <span className='mx-2'>
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='20'
                                        height='20'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='#0E9F6E'
                                        stroke-width='2'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-circle-check'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                                        <path d='M9 12l2 2l4 -4' />
                                      </svg>
                                    </span>
                                    <span className='fs-7 me-2' style={{color: '#5A5A5A'}}>
                                      Completed
                                    </span>
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className='d-flex justify-content-end '>
                                <div className='pt-4'>
                                  <span
                                    className='badge rounded-pill p-1'
                                    style={{border: '1px solid #C5C5C5'}}
                                  >
                                    <span className='mx-2'>
                                      <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='18'
                                        height='18'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='#007bff'
                                        stroke-width='2'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-clock'
                                      >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                                        <path d='M12 7v5l3 3' />
                                      </svg>
                                    </span>
                                    <span className='fs-7 me-2' style={{color: '#5A5A5A'}}>
                                      Pending
                                    </span>
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ))}

                {activeTab === 'assets' && (
                  <>
                    {assets && assets.length > 0 ? (
                      assets
                        .filter((asset) => asset.is_repo !== true)
                        .map((asset) => (
                          <div
                            className='mt-8'
                            style={{cursor: 'pointer'}}
                            onClick={() => navigate('/asset-management')}
                          >
                            <div className={`card mb-5 mb-xl-8`}>
                              <div className='card-header border-0 pt-5 mb-5'>
                                <div className='d-flex align-items-center'>
                                  <div className='d-flex justify-content-start flex-column'>
                                    <span className='card-label fw-bold fs-6'>{asset.name}</span>
                                    <span
                                      className='fw-semibold fs-7 d-none d-md-block'
                                      style={{color: '#5A5A5A'}}
                                    >
                                      model: {asset.model} | OS: {asset.os_version}
                                    </span>
                                  </div>
                                </div>

                                <div className='d-flex justify-content-end align-items-center'>
                                  <div
                                    className='fs-7 m-2 text-gray-700'
                                    style={{cursor: 'pointer'}}
                                    onClick={() => navigate('/asset-management')}
                                  >
                                    View in Asset Management{' '}
                                    <img
                                      src='/media/buttons/Chevron Button.svg'
                                      alt='>'
                                      className='ms-2'
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div
                        className='d-flex justify-content-center align-items-center text-center mt-20'
                        style={{height: '100%'}}
                      >
                        <div className=''>
                          <img
                            src='/media/buttons/emptystate.svg'
                            alt=''
                            className='img-fluid'
                            style={{maxWidth: '100px'}}
                          />

                          <div className=' fs-6'>
                            <p style={{fontWeight: 'bold'}}>No Assets Added</p>
                          </div>
                          <p className='fs-7'>
                            No assets are currently assigned to this employee. You can assign assets
                            by visiting the{' '}
                            <a
                              href='/asset-management'
                              className='fw-semibold'
                              style={{color: '#0E9F6E'}}
                            >
                              Asset Management
                            </a>{' '}
                            section.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {activeTab === 'offboarding' &&
                  users.map((user) => (
                    <>
                      <OffboardingChecklist user={user} />
                    </>
                  ))}
              </div>
            </>
          ))}
        </div>
      </Drawer>
      <ToastContainer />
      {isRoleChangeModalOpen && roleChangeData && (
        <ConfirmationModal
          isModalOpen={isRoleChangeModalOpen}
          hideModal={() => {
            setIsRoleChangeModalOpen(false)
            // Reset the select value to original role if canceled
            const select = document.querySelector(
              `select[data-user-id="${roleChangeData.userId}"]`
            ) as HTMLSelectElement
            if (select) {
              select.value = roleChangeData.originalRole
            }
            setRoleChangeData(null)
          }}
          confirmBtnClickHandler={handleConfirmRoleChange}
          toDeleteItemTitle={roleChangeData.newRole}
          isLoading={updating}
          title='Change Role'
          message={
            roleChangeData.newRole === 'Member'
              ? `Confirm role change to '${roleChangeData.newRole}'? This will remove ownership of all associated items: tests, controls, policies, risks, vendors, and more. The user will lose access to the admin dashboard.`
              : `Confirm role change to '${roleChangeData.newRole}'? The user will gain access to the admin dashboard with this change.`
          }
          type='default'
          confirmButtonText='Change'
        />
      )}
      {isTaskDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isTaskDeleteModalOpen}
          hideModal={() => {
            setIsTaskDeleteModalOpen(false)
            setSelectedTaskId(null)
          }}
          confirmBtnClickHandler={handleConfirmTaskDelete}
          toDeleteItemTitle='selected task'
          isLoading={isTaskDeleting}
          title='Remove Task'
          message='Are you sure you want to remove this task? This action cannot be undone.'
          type='delete'
          confirmButtonText='Remove'
        />
      )}
    </>
  )
}

export default ViewPersonnel
